import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
  Avatar,
  useTheme,
  Fade,
  Divider
} from "@mui/material";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import { alpha } from "@mui/material/styles";

const countries = [
  { code: "us", label: "United States" },
  { code: "in", label: "India" },
  { code: "au", label: "Australia" },
  { code: "ca", label: "Canada" },
  { code: "de", label: "Germany" },
  // ...add more as needed
];

const bestTimes = [
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" }
];

export default function RequestForm({ open, onClose, onSubmit }) {
  const theme = useTheme();
  const [form, setForm] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    country: "",
    numberOfEmployees: "",
    bestTimeToConnect: "",
    reasonForRequest: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setForm({
      fullName: "",
      phoneNumber: "",
      email: "",
      country: "",
      numberOfEmployees: "",
      bestTimeToConnect: "",
      reasonForRequest: ""
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(form);
    handleReset();
    onClose();
  };

  const accentColor = "#3E7BFA";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "visible",
          boxShadow: `0 8px 40px ${alpha(accentColor, 0.18)}`,
          background: `linear-gradient(135deg, #f8fafc 70%, ${alpha(accentColor, 0.07)} 100%)`,
          position: "relative"
        }
      }}
      TransitionComponent={Fade}
      transitionDuration={500}
    >
      <Box
        sx={{
          position: "absolute",
          top: -44,
          left: "50%",
          transform: "translateX(-50%)",
          bgcolor: accentColor,
          width: 88,
          height: 88,
          borderRadius: "50%",
          boxShadow: `0 4px 24px ${alpha(accentColor, 0.25)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
        }}
      >
        <Avatar
          sx={{
            bgcolor: "#fff",
            width: 64,
            height: 64,
            boxShadow: `0 2px 8px ${alpha(accentColor, 0.18)}`,
          }}
        >
          <ContactSupportIcon sx={{ color: accentColor, fontSize: 38 }} />
        </Avatar>
      </Box>
      <DialogTitle
        sx={{
          pt: 7,
          pb: 1,
          textAlign: "center",
          fontWeight: 800,
          fontSize: "2rem",
          color: accentColor,
          letterSpacing: "-0.02em",
        }}
      >
        Enquiry for <Box component="span" sx={{ color: "#111827" }}>Orucore</Box>
      </DialogTitle>
      <Typography
        variant="subtitle2"
        sx={{
          textAlign: "center",
          color: "#64748b",
          mb: 2,
          fontWeight: 500,
          letterSpacing: 0.2,
        }}
      >
        Our team will get in touch with you soon!
      </Typography>
      <Divider sx={{ mb: 2, mx: 3, borderColor: alpha(accentColor, 0.12) }} />
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pb: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: "#fff" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: "#fff" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                type="email"
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: "#fff" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Country"
                name="country"
                value={form.country}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: "#fff" }
                }}
              >
                {countries.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Number of Employees"
                name="numberOfEmployees"
                value={form.numberOfEmployees}
                onChange={handleChange}
                fullWidth
                type="number"
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: "#fff" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Best time to Connect"
                name="bestTimeToConnect"
                value={form.bestTimeToConnect}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: "#fff" }
                }}
              >
                {bestTimes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reason for Request"
                name="reasonForRequest"
                value={form.reasonForRequest}
                onChange={handleChange}
                required
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: "#fff" }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, justifyContent: "space-between" }}>
          <Button onClick={handleReset} color="secondary" sx={{ fontWeight: 600 }}>
            Reset
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onClose} sx={{ fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: `linear-gradient(90deg, ${accentColor}, #6E9DFF)`,
                fontWeight: 700,
                px: 4,
                borderRadius: 2,
                boxShadow: `0 4px 16px ${alpha(accentColor, 0.12)}`,
                "&:hover": {
                  background: `linear-gradient(90deg, #3667E0, #5E89F0)`,
                  boxShadow: `0 8px 24px ${alpha(accentColor, 0.18)}`
                }
              }}
            >
              Submit
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
}